import { motion } from "framer-motion";
import { CalendarDays, Clock, MapPin, Shirt } from "lucide-react";
import { SectionHeading } from "./Story";

const fmt = (d) => {
  try { return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return d; }
};

export const Events = ({ events = [] }) => (
  <section id="events" className="py-20 sm:py-28 lg:py-32 bg-[var(--surface)]" data-testid="events-section">
    <div className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-16">
      <SectionHeading script="save the date" title="Wedding Events" testid="events-heading" />

      <div className="relative">
        <div className="hidden sm:block absolute left-1/2 top-0 bottom-0 w-px bg-[var(--border-gold)] -translate-x-1/2" />
        <div className="space-y-5 sm:space-y-8">
          {events.map((ev, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className={`sm:grid sm:grid-cols-2 sm:gap-12 items-center ${i % 2 ? "sm:[&>*:first-child]:order-2" : ""}`}
              data-testid={`event-card-${i}`}
            >
              <div className={`relative sm:text-left ${i % 2 ? "sm:pl-12" : "sm:pr-12"}`}>
                <div className="hidden sm:block absolute top-2 w-3 h-3 rounded-full bg-[var(--gold)] ring-4 ring-[var(--surface)]"
                  style={i % 2 ? { left: "-6px" } : { right: "-6px" }} />
                <div className="bg-[var(--card)] rounded-xl p-4 sm:p-5 shadow-sm border border-[var(--border)]">
                  <h3 className="font-serif text-lg sm:text-xl text-[var(--burgundy)]" data-testid={`event-title-${i}`}>
                    {ev.title}
                  </h3>
                  {ev.description && <p className="mt-1.5 text-[var(--text-secondary)] text-sm leading-relaxed">{ev.description}</p>}
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-[var(--text-primary)]">
                    <span className="flex items-center gap-1.5" data-testid={`event-datetime-${i}`}>
                      <CalendarDays className="w-4 h-4 text-[var(--gold)] shrink-0" />{fmt(ev.date)}
                      {ev.time && <><span className="text-[var(--border-gold)]">·</span><Clock className="w-4 h-4 text-[var(--gold)] shrink-0" />{ev.time}</>}
                    </span>
                    {ev.show_location && (ev.venue || ev.address) && (
                      <span className="flex items-center gap-1.5" data-testid={`event-location-${i}`}>
                        <MapPin className="w-4 h-4 text-[var(--gold)] shrink-0" />{[ev.venue, ev.address].filter(Boolean).join(", ")}
                      </span>
                    )}
                    {ev.dress_code && (
                      <span className="flex items-center gap-1.5" data-testid={`event-dresscode-${i}`}>
                        <Shirt className="w-4 h-4 text-[var(--gold)] shrink-0" />
                        <span><span className="text-[var(--text-muted)]">Dress code: </span>{ev.dress_code}</span>
                      </span>
                    )}
                  </div>
                  {ev.functions?.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-[var(--border)] space-y-1" data-testid={`event-functions-${i}`}>
                      {ev.functions.map((fn, k) => (
                        <div key={k} className="text-sm">
                          <span className="text-[var(--gold-hover)] font-medium">{fn.time}</span>
                          <span className="text-[var(--text-primary)]">{fn.time && fn.name ? " · " : ""}{fn.name}</span>
                          {fn.note && <span className="text-[var(--text-muted)]"> — {fn.note}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="hidden sm:block" />
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  </section>
);
